import { Copy, ShoppingCart } from "lucide-react";

import { useProjectShortfall } from "@/api/queries";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { EmptyState, LoadingState } from "@/components/States";
import { useToast } from "@/components/toast-context";
import { formatQuantity, formatUnit } from "@/lib/format";

/**
 * What has to be bought (Frontend Spec §18, "Créer liste d'achat").
 *
 * Purchasing lands in PHASE 10. Until it does, this shows and copies the list
 * rather than pretending to raise an order — an interface that looked like it
 * had ordered something would be worse than one that plainly has not.
 */
export function ShortfallDialog({
  open,
  onOpenChange,
  projectId,
  projectCode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectCode: string;
}) {
  const toast = useToast();
  const { data, isLoading } = useProjectShortfall(projectId, { enabled: open });

  const text = (data ?? [])
    .map(
      (line) =>
        `${formatQuantity(line.missing)} ${formatUnit(line.unit)}\t${line.product_name}${
          line.product_part_number ? `\t${line.product_part_number}` : ""
        }`,
    )
    .join("\n");

  async function copy() {
    try {
      await navigator.clipboard.writeText(`${projectCode}\n${text}`);
      toast.success("Liste copiée");
    } catch {
      toast.error("Copie refusée par le navigateur");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Liste d'achat"
      description="Ce qui manque après avoir réservé tout ce qui était libre."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button variant="primary" onClick={() => void copy()} disabled={!text}>
            <Copy className="size-4" aria-hidden />
            Copier
          </Button>
        </>
      }
    >
      {isLoading && <LoadingState label="Calcul du manquant…" />}
      {data && data.length === 0 && (
        <EmptyState
          icon={<ShoppingCart className="size-6" aria-hidden />}
          title="Rien à acheter"
          description="Tout ce que ce chantier demande existe et peut être réservé."
        />
      )}
      {data && data.length > 0 && (
        <ul className="flex flex-col divide-y divide-border/60">
          {data.map((line) => (
            <li key={line.requirement_id} className="flex items-baseline gap-3 py-2">
              <span className="tnum w-24 shrink-0 whitespace-nowrap text-right text-[14px] font-semibold text-status-out">
                {formatQuantity(line.missing)}
                <span className="ml-1 text-[11px] font-normal text-ink-subtle">
                  {formatUnit(line.unit)}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[13px] text-ink">{line.product_name}</span>
                <span className="block truncate text-[11px] text-ink-subtle">
                  {[line.product_manufacturer, line.product_part_number]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
